import * as React from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import {
  FinanceDimensionValueMembersDocument,
  FinanceGetDimensionDocument,
  GetLegalEntityFinanceTaxTypesDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const RootPage = React.lazy(() => import('modules/finance/pages/settings/RootPage'))

const BankAccountPage = React.lazy(() => import('modules/finance/pages/Accounting/BankAccountPage'))
const CustomCategoriesPage = React.lazy(
  () => import('modules/finance/pages/Accounting/CustomCategoriesPage')
)
const VendorPage = React.lazy(() => import('modules/finance/pages/Accounting/VendorPage'))

const CostCentersPage = React.lazy(() => import('modules/finance/pages/CostCentersPage'))
const CostCentersListPage = React.lazy(() => import('modules/finance/pages/CostCentersListPage'))
const CostCentersListPageEdit = React.lazy(
  () => import('modules/finance/pages/CostCentersListPage/Edit')
)
const CostCentersListPageDelete = React.lazy(
  () => import('modules/finance/pages/CostCentersListPage/Delete')
)
const CostCentersListPageImport = React.lazy(
  () => import('modules/finance/pages/CostCentersListPage/Import')
)
const CostCentersPeoplePage = React.lazy(
  () => import('modules/finance/pages/CostCentersPeoplePage')
)
const CostCentersPeopleDetailsPage = React.lazy(
  () => import('modules/finance/pages/CostCentersPeopleDetailsPage')
)
const CostCentersPeopleCreatePage = React.lazy(
  () => import('modules/finance/pages/CostCenterPeopleCreatePage')
)

const DimensionDetailPage = React.lazy(() => import('modules/finance/pages/DimensionDetailPage'))
const DimensionsImportPage = React.lazy(() => import('modules/finance/pages/DimensionImportPage'))
const DimensionValueDetailPage = React.lazy(
  () => import('modules/finance/pages/DimensionValueDetailPage')
)
const CreateCostCenterModalPage = React.lazy(
  () => import('modules/finance/pages/CostCentersListPage/Create')
)
const FinanceSettingsPage = React.lazy(
  () => import('modules/finance/pages/settings/FinanceSettingsPage')
)
const SalesSettingsPage = React.lazy(() => import('modules/finance/pages/settings/Sales/index'))

const AccountingSettingsPage = React.lazy(
  () => import('modules/finance/pages/settings/Acccounting')
)

const DefaultConfigurationPage = React.lazy(
  () => import('modules/finance/pages/Accounting/DefaultConfigurationPage')
)
const TaxTypeDetailPage = React.lazy(
  () => import('modules/finance/pages/Accounting/TaxTypeDetailPage')
)
const TaxTypePage = React.lazy(() => import('modules/finance/pages/Accounting/TaxTypePage'))
const PayrollConceptPage = React.lazy(
  () => import('modules/finance/pages/Accounting/PayrollConceptPage')
)

const DimensionDetailHeader = React.lazy(
  () => import('modules/finance/pages/DimensionDetailPage/Header')
)

const DimensionValueDetailHeader = React.lazy(
  () => import('modules/finance/pages/DimensionValueDetailPage/Header')
)

const DeleteDimensionModal = React.lazy(
  () => import('modules/finance/pages/DimensionDetailPage/DimensionDetail/DeleteDimensionModal')
)

const RenameDimensionModal = React.lazy(
  () => import('modules/finance/pages/DimensionDetailPage/DimensionDetail/RenameDimensionModal')
)

const EditDimensionValuePanel = React.lazy(
  () => import('modules/finance/pages/DimensionValueDetailPage/EditValue')
)

const DeleteDimensionValue = React.lazy(
  () => import('modules/finance/pages/DimensionValueDetailPage/DeleteValue')
)

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.finance.path,
      element: <RootPage />,
      children: [
        {
          index: true,
          element: <Navigate to={navigation.settings.finance.dimensions.path} replace />,
        },
        {
          path: navigation.settings.finance.dimensions.path,
          element: <FinanceSettingsPage />,
          handle: {
            meta: () => ({
              title: i18n.t('finance.settings.dimensions.title'),
            }),
          },
          children: [
            {
              path: navigation.settings.finance.dimensions.dimension.path,
              element: <DimensionDetailPage />,
              handle: {
                resource: true,
                meta: ({ params, watchQuery, session }) =>
                  watchQuery({
                    query: FinanceGetDimensionDocument,
                    variables: { id: Number(params['id']), companyId: session.company.id },
                  }).map(({ data }) => {
                    const dimension = data?.finance.customDimensions[0]

                    if (!dimension) {
                      return
                    }

                    return {
                      title: dimension.name,
                      headerComponent: <DimensionDetailHeader dimension={dimension} />,
                    }
                  }),
              },
              children: [
                {
                  path: navigation.settings.finance.dimensions.dimension.values.path,
                  element: <DimensionValueDetailPage />,
                  handle: {
                    resource: true,
                    meta: ({ params, watchQuery, session }) =>
                      watchQuery({
                        query: FinanceDimensionValueMembersDocument,
                        variables: {
                          dimensionValueId: Number(params['value_id']),
                          companyId: session.company.id,
                        },
                      }).map(({ data }) => {
                        const dimension = data?.finance.customDimensionValue

                        if (!dimension) {
                          return
                        }

                        return {
                          title: dimension.name,
                          headerComponent: <DimensionValueDetailHeader dimension={dimension} />,
                        }
                      }),
                  },
                  children: [
                    {
                      path: navigation.settings.finance.dimensions.dimension.values.edit.path,
                      element: <EditDimensionValuePanel />,
                    },
                    {
                      path: navigation.settings.finance.dimensions.dimension.values.delete.path,
                      element: <DeleteDimensionValue />,
                    },
                  ],
                },
                {
                  path: navigation.settings.finance.dimensions.dimension.delete.path,
                  element: <DeleteDimensionModal />,
                },
                {
                  path: navigation.settings.finance.dimensions.dimension.edit.path,
                  element: <RenameDimensionModal />,
                },
                {
                  path: navigation.settings.finance.dimensions.dimension.import.path,
                  element: <DimensionsImportPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.settings.finance.sales.path,
          element: <Outlet />,
          handle: {
            meta: () => ({
              title: i18n.t('finance.settings.sales.title'),
            }),
          },
          children: [
            {
              index: true,
              element: <SalesSettingsPage />,
            },
          ],
        },
        {
          path: navigation.settings.finance.accounting.path,
          handle: {
            meta: () => ({
              title: i18n.t('finance.settings.accounting.title'),
            }),
          },
          children: [
            {
              index: true,
              element: <AccountingSettingsPage />,
            },
            {
              path: navigation.settings.finance.accounting.expenseCategory.path,
              element: <CustomCategoriesPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.custom_categories.label'),
                  illustration: 'checks',
                  description: i18n.t('finance.settings.accounting.custom_categories.description'),
                }),
              },
            },
            {
              path: navigation.settings.finance.accounting.bankAccount.path,
              element: <BankAccountPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.bank_account.label'),
                  description: i18n.t('finance.settings.accounting.bank_account.description'),
                }),
              },
            },
            {
              path: navigation.settings.finance.accounting.vendor.path,
              element: <VendorPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.vendor.label'),
                  description: i18n.t('finance.settings.accounting.vendor.description'),
                }),
              },
            },
            {
              path: navigation.settings.finance.accounting.taxType.path,
              element: <TaxTypePage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.tax_type.label'),
                  description: i18n.t('finance.settings.accounting.tax_type.description'),
                  illustration: 'coins',
                }),
              },
              children: [
                {
                  path: navigation.settings.finance.accounting.taxType.taxTypeId.path,
                  element: <TaxTypeDetailPage />,
                  handle: {
                    meta: ({ params, watchQuery }) =>
                      watchQuery({
                        query: GetLegalEntityFinanceTaxTypesDocument,
                        variables: {
                          ids: [Number(params['id'])],
                        },
                      }).map(({ data }) => {
                        const taxType = data?.finance?.taxTypes?.at(0)
                        const taxTypeName = taxType ? taxType.name : ''
                        return {
                          title: taxTypeName,
                          illustration: 'documents',
                          description: i18n.t('finance.settings.taxes.tax_type.description'),
                        }
                      }),
                  },
                },
              ],
            },
            {
              path: navigation.settings.finance.accounting.defaultConfiguration.path,
              element: <DefaultConfigurationPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.default_configuration.label'),
                  description: i18n.t(
                    'finance.settings.accounting.default_configuration.description'
                  ),
                  illustration: 'settings',
                }),
              },
            },
            {
              path: navigation.settings.finance.accounting.payrollConcept.path,
              element: <PayrollConceptPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.accounting.payroll_concept.label'),
                  description: i18n.t('finance.settings.accounting.payroll_concept.description'),
                }),
              },
            },
          ],
        },
        {
          path: navigation.settings.finance.costCenters.path,
          element: <CostCentersPage />,
          handle: {
            resource: true,
            meta: () => {
              return {
                title: i18n.t('finance.settings.cost_centers.title'),
                subNavigation: [
                  {
                    id: 'list',
                    isVisible: true,
                    title: i18n.t('finance.settings.cost_centers.list'),
                    href: navigation.settings.finance.costCenters.list.fullPath(),
                  },
                  {
                    id: 'people',
                    isVisible: true,
                    title: i18n.t('finance.settings.cost_centers.people'),
                    href: navigation.settings.finance.costCenters.people.fullPath(),
                  },
                ],
              }
            },
          },
          children: [
            {
              index: true,
              element: <ReroutingIndex />,
            },
            {
              path: navigation.settings.finance.costCenters.list.path,
              element: <CostCentersListPage />,
              children: [
                {
                  path: navigation.settings.finance.costCenters.list.newCostCenter.path,
                  element: <CreateCostCenterModalPage />,
                },
                {
                  path: navigation.settings.finance.costCenters.delete.path,
                  element: <CostCentersListPageDelete />,
                },
                {
                  path: navigation.settings.finance.costCenters.list.edit.path,
                  element: <CostCentersListPageEdit />,
                },
                {
                  path: navigation.settings.finance.costCenters.list.import.path,
                  element: (
                    <Routes>
                      <Route path="" element={<CostCentersListPageImport />} />
                    </Routes>
                  ),
                },
              ],
            },
            {
              path: navigation.settings.finance.costCenters.people.path,
              element: <CostCentersPeoplePage />,
              handle: {
                meta: () => ({
                  title: i18n.t('finance.settings.cost_centers.title'),
                }),
              },
              children: [
                {
                  path: navigation.settings.finance.costCenters.people.create.path,
                  element: (
                    <Routes>
                      <Route path="" element={<CostCentersPeopleCreatePage />} />
                    </Routes>
                  ),
                },
                {
                  path: navigation.settings.finance.costCenters.people.edit.path,
                  element: <CostCentersPeopleDetailsPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
